import { List, Show, Create, Edit, Datagrid, TextField, DateField, ChipField, SimpleForm, TextInput, SelectInput, Button, Filter, SearchInput, useGetList, useUpdate, useNotify, useRefresh, ReferenceInput } from 'react-admin';
import { useState, useEffect } from 'react';
import { Box, Typography, Paper, Chip, Stepper, Step, StepLabel, LinearProgress, Alert, Grid, Divider, Avatar, Card, CardContent, Table, TableBody, TableCell, TableHead, TableRow, List as MuiList, ListItem, ListItemIcon, ListItemText, useMediaQuery, TablePagination, CircularProgress, IconButton } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Warning, Gavel, CheckCircle, Schedule, Cancel, ArrowForward, Timeline, Person, Assignment, TrendingUp, TrendingDown, Search, HourglassEmpty, Close } from '@mui/icons-material';
import { ListPageHeader } from '@/components/ListPageHeader';
import { WizardListToolbar } from '@/components/workflow/WizardListToolbar';
import { ProcessActionsGrouped } from '@/components/workflow/ProcessActions';
import { useEmployeeMap, getEmployeeName } from '@/hooks/useEmployeeMap';
import type { DisciplinaryRecord } from '@/types/entities';

const WARNING_TYPES = ['Verbal', 'Written', 'FinalWritten'];
const CASE_STAGES = ['Investigation', 'Hearing', 'Appeal', 'Closed'];
const OUTCOMES = ['Guilty', 'NotGuilty', 'Withdrawn'];
const LRA_CHECKLIST_ITEMS = [
  { id: 'noticeIssued', label: 'Notice to attend hearing issued' },
  { id: 'representativeInformed', label: 'Employee informed of right to representative' },
  { id: 'hearingConducted', label: 'Hearing conducted within 30 days' },
  { id: 'outcomeCommunicated', label: 'Outcome communicated in writing' },
  { id: 'sanctionDocumented', label: 'Sanction documented if guilty' },
];
const STAGE_COLORS: Record<string, string> = {
  Investigation: 'warning.main',
  Hearing: 'info.main',
  Appeal: 'default',
  Closed: 'success.main',
};
const STAGE_BG: Record<string, string> = {
  Investigation: 'warning.light',
  Hearing: 'info.light',
  Appeal: 'grey.100',
  Closed: 'success.light',
};

const WARNING_COLORS: Record<string, string> = {
  Verbal: '#2e7d32',
  Written: '#ed6c02',
  FinalWritten: '#c62828',
};

const CaseStageSparkline = ({ currentStage, incidentDate }: { currentStage: string; incidentDate: string }) => {
  const [now] = useState(() => Date.now());

  if (currentStage === 'Closed') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CheckCircle sx={{ fontSize: 14, color: 'success.main' }} />
        <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>
          Closed
        </Typography>
      </Box>
    );
  }
  const incident = new Date(incidentDate).getTime();
  const daysElapsed = Math.ceil((now - incident) / (1000 * 60 * 60 * 24));
  const maxDays = 30;
  const pctElapsed = Math.min(100, (daysElapsed / maxDays) * 100);

  const getColor = () => {
    if (currentStage === 'Appeal') return 'warning';
    if (daysElapsed > 30) return 'error';
    if (daysElapsed > 14) return 'warning';
    return 'success';
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}>
      <Box sx={{ flex: 1, position: 'relative' }}>
        <Box sx={{ height: 8, bgcolor: 'grey.200', borderRadius: 1, overflow: 'hidden' }}>
          <Box
            sx={{
              width: `${pctElapsed}%`,
              height: '100%',
              bgcolor: `${getColor()}.main`,
              borderRadius: 1,
              transition: 'width 0.3s ease',
            }}
          />
        </Box>
        <Box sx={{ position: 'absolute', right: 0, top: -2 }}>
          <TrendingDown sx={{ fontSize: 12, color: `${getColor()}.main` }} />
        </Box>
      </Box>
      <Typography variant="caption" sx={{ minWidth: 40, textAlign: 'right', color: `${getColor()}.main`, fontWeight: 600 }}>
        {daysElapsed}d
      </Typography>
    </Box>
  );
};

export const DisciplinaryList = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const { data: cases, isLoading } = useGetList('disciplinary-cases', { pagination: { page: page + 1, perPage: rowsPerPage } });
  const navigate = useNavigate();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const employeeMap = useEmployeeMap();

  const stats = {
    total: cases?.length ?? 0,
    investigation: cases?.filter((c: DisciplinaryRecord) => c.currentStage === 'Investigation').length ?? 0,
    hearing: cases?.filter((c: DisciplinaryRecord) => c.currentStage === 'Hearing').length ?? 0,
    appeal: cases?.filter((c: DisciplinaryRecord) => c.currentStage === 'Appeal').length ?? 0,
    closed: cases?.filter((c: DisciplinaryRecord) => c.currentStage === 'Closed').length ?? 0,
  };

  const warningTypeCounts = cases?.reduce((acc: Record<string, number>, c: DisciplinaryRecord) => {
    if (c.warningType) {
      acc[c.warningType] = (acc[c.warningType] || 0) + 1;
    }
    return acc;
  }, {}) ?? {};

  const pieData = Object.entries(warningTypeCounts).map(([label, value]) => ({ label, value: Number(value) }));

  if (isMobile) {
    return (
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <WizardListToolbar domainId="hr.industrial-relations" wizardPath="wizard/disciplinary" label="New Disciplinary Case" />
        </Box>
        <ListPageHeader
          title="Disciplinary Cases"
          metrics={[
            { label: 'Investigation', value: String(stats.investigation), color: 'warning.main' },
            { label: 'Hearing', value: String(stats.hearing), color: 'info.main' },
            { label: 'Closed', value: String(stats.closed), color: 'success.main' },
          ]}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
          {cases?.map((c: DisciplinaryRecord) => (
            <Paper key={c.id} sx={{ p: 2 }} onClick={() => navigate(`/disciplinary-cases/${c.id}/show`)}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ width: 40, height: 40, bgcolor: STAGE_COLORS[c.currentStage || '']?.replace('.main', '') || 'grey.500', fontSize: 14 }}>
                    <Gavel sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>{getEmployeeName(employeeMap, c.employee?.id)}</Typography>
                    <Typography variant="caption" color="text.secondary">{c.caseNumber}</Typography>
                  </Box>
                </Box>
                <Chip label={c.currentStage} size="small" sx={{ bgcolor: STAGE_COLORS[c.currentStage || ''], color: 'white' }} />
              </Box>
              <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                <Typography variant="caption" color="text.secondary">{c.warningType}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">{c.incidentDate}</Typography>
                <CaseStageSparkline currentStage={c.currentStage || 'Investigation'} incidentDate={c.incidentDate || ''} />
              </Box>
            </Paper>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <WizardListToolbar domainId="hr.industrial-relations" wizardPath="wizard/disciplinary" label="New Disciplinary Case" />
      </Box>
      <ListPageHeader
        title="Disciplinary Cases"
        metrics={[
          { label: 'Investigation', value: String(stats.investigation), color: 'warning.main' },
          { label: 'Hearing', value: String(stats.hearing), color: 'info.main' },
          { label: 'Appeal', value: String(stats.appeal), color: 'default' },
          { label: 'Closed', value: String(stats.closed), color: 'success.main' },
        ]}
        pieChartData={pieData}
        pieChartTitle="Warning Type Distribution"
        barChartData={[stats.investigation, stats.hearing, stats.appeal, stats.closed]}
        barChartLabels={['Investigation', 'Hearing', 'Appeal', 'Closed']}
        barChartTitle="Cases by Stage"
      />
      <Paper sx={{ mt: 2, overflow: 'hidden' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 600 }}>Employee</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Case #</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Incident Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Stage</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Warning</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Outcome</TableCell>
              <TableCell sx={{ fontWeight: 600, minWidth: 140 }}>Duration</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
          {cases?.map((c: DisciplinaryRecord) => (
              <TableRow
                key={c.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(`/disciplinary-cases/${c.id}/show`)}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: STAGE_COLORS[c.currentStage || '']?.replace('.main', '') || 'grey.500', fontSize: 12 }}>
                      <Gavel sx={{ fontSize: 14 }} />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>{getEmployeeName(employeeMap, c.employee?.id)}</Typography>
                      <Typography variant="caption" color="text.secondary">{c.employee?.id}</Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{c.caseNumber}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{c.incidentDate}</Typography>
                </TableCell>
                <TableCell>
                  <Chip label={c.currentStage} size="small" sx={{ bgcolor: STAGE_COLORS[c.currentStage || ''], color: 'white' }} />
                </TableCell>
                <TableCell>
                  <Chip
                    label={c.warningType}
                    size="small"
                    sx={{ bgcolor: `${WARNING_COLORS[c.warningType || ''] || ''}20`, color: WARNING_COLORS[c.warningType || ''] || '', fontWeight: 500 }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{c.outcome || 'Pending'}</Typography>
                </TableCell>
                <TableCell>
                  <CaseStageSparkline currentStage={c.currentStage || 'Investigation'} incidentDate={c.incidentDate || ''} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={cases?.length ?? 0}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
        />
        {(!cases || cases.length === 0) && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Gavel sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
            <Typography color="text.secondary">No disciplinary cases found</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export const DisciplinaryFilters = () => (
  <Filter>
    <SearchInput source="q" placeholder="Search case # or employee" />
    <SelectInput source="currentStage" choices={CASE_STAGES.map(s => ({ id: s, name: s }))} alwaysOn />
    <SelectInput source="warningType" choices={WARNING_TYPES.map(w => ({ id: w, name: w }))} />
    <SelectInput source="outcome" choices={OUTCOMES.map(o => ({ id: o, name: o }))} />
  </Filter>
);

const DisciplinaryShowContent = ({ record }: { record: DisciplinaryRecord | undefined }) => {
  const navigate = useNavigate();
  const [now] = useState(() => Date.now());
  const [update, { isLoading: isUpdating }] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();

  const checklistDerived: Record<string, boolean> = {
    hearingConducted: record?.currentStage !== 'Investigation',
    outcomeCommunicated: !!record?.outcome,
    sanctionDocumented: record?.outcome === 'Upheld',
  };

  const savedList: string[] = (record as { lraChecklist?: string[] } | undefined)?.lraChecklist || [];

  const getDisplayState = (): Record<string, boolean> => {
    const state: Record<string, boolean> = {};
    LRA_CHECKLIST_ITEMS.forEach(({ id }) => {
      if (savedList.includes(id)) {
        state[id] = true;
      } else if (savedList.includes(`!${id}`)) {
        state[id] = false;
      } else {
        state[id] = checklistDerived[id] ?? false;
      }
    });
    return state;
  };

  const [displayState, setDisplayState] = useState<Record<string, boolean>>(
    () => getDisplayState()
  );

  useEffect(() => {
    setDisplayState(getDisplayState());
  }, [(record as { lraChecklist?: string[]; currentStage?: string; outcome?: string } | undefined)?.lraChecklist, (record as { lraChecklist?: string[]; currentStage?: string; outcome?: string } | undefined)?.currentStage, (record as { lraChecklist?: string[]; currentStage?: string; outcome?: string } | undefined)?.outcome]);

  if (!record) return null;
  const stageIdx = CASE_STAGES.indexOf(record.currentStage ?? '');
  const nextStage = stageIdx < CASE_STAGES.length - 1 ? CASE_STAGES[stageIdx + 1] : null;

  const advanceStage = (overrides?: Record<string, unknown>) => {
    if (!nextStage && !overrides) return;
    update(
      'disciplinary-cases',
      { id: record.id, data: { currentStage: nextStage || record.currentStage, ...overrides }, previousData: record },
      {
        onSuccess: () => {
          notify('Case advanced', { type: 'success' });
          refresh();
        },
        onError: (error: Error) => {
          notify(`Error: ${error.message || 'Failed to update case'}`, { type: 'error' });
        },
      }
    );
  };

  const recordOutcome = (outcome: string) => {
    update(
      'disciplinary-cases',
      { id: record.id, data: { outcome, currentStage: 'Closed' }, previousData: record },
      {
        onSuccess: () => {
          notify(`Outcome recorded as ${outcome}`, { type: 'success' });
          refresh();
        },
        onError: (error: Error) => {
          notify(`Error: ${error.message || 'Failed to record outcome'}`, { type: 'error' });
        },
      }
    );
  };

  const toggleChecklistItem = (id: string) => {
    const newVal = !displayState[id];
    const prev = displayState[id];
    setDisplayState(prevState => ({ ...prevState, [id]: newVal }));

    const updated: string[] = savedList.filter((x: string) => x !== id && x !== `!${id}`);
    if (newVal && !checklistDerived[id]) {
      updated.push(id);
    } else if (!newVal && checklistDerived[id]) {
      updated.push(`!${id}`);
    }

    update(
      'disciplinary-cases',
      { id: record.id, data: { lraChecklist: updated }, previousData: record },
      {
        onSuccess: () => {
          notify('Checklist updated', { type: 'success' });
        },
        onError: (error: Error) => {
          notify(`Error: ${error.message || 'Failed to update checklist'}`, { type: 'error' });
          setDisplayState(prevState => ({ ...prevState, [id]: prev }));
        },
      }
    );
  };
  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <IconButton onClick={() => navigate('/disciplinary-cases')} size="small" sx={{ color: 'text.secondary' }}>
          <Close />
        </IconButton>
      </Box>
      <Paper elevation={0} sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #1a237e, #283593)', color: 'primary.contrastText', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
          <Avatar sx={{ width: 64, height: 64, bgcolor: 'rgba(255,255,255,0.2)', fontSize: 24 }}>
            <Gavel sx={{ fontSize: 32 }} />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography variant="h5" fontWeight={700}>Disciplinary Case — {record.caseNumber || 'N/A'}</Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>{record.employee?.id || 'Unknown Employee'}</Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
              <Chip label={record.currentStage} size="small" sx={{ bgcolor: STAGE_COLORS[record.currentStage ?? ''], color: 'white', fontWeight: 600 }} />
              {record.warningType && <Chip label={record.warningType} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />}
            </Box>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>Case Duration</Typography>
            <Typography variant="h6" fontWeight={700}>{Math.ceil((now - new Date(record.incidentDate ?? '').getTime()) / (1000 * 60 * 60 * 24))} days</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 3, mt: 2, pt: 2, borderTop: '1px solid rgba(255,255,255,0.2)', flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: 120 }}>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>Case Number</Typography>
            <Typography variant="body1" fontWeight={600}>{record.caseNumber || '—'}</Typography>
          </Box>
          <Box sx={{ flex: 1, minWidth: 120 }}>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>Warning Type</Typography>
            <Typography variant="body1" fontWeight={600}>{record.warningType || '—'}</Typography>
          </Box>
          <Box sx={{ flex: 1, minWidth: 120 }}>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>Incident Date</Typography>
            <Typography variant="body1" fontWeight={600}>{record.incidentDate || '—'}</Typography>
          </Box>
          <Box sx={{ flex: 1, minWidth: 120, textAlign: 'right' }}>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>Outcome</Typography>
            <Typography variant="h6" fontWeight={700}>{record.outcome || 'Pending'}</Typography>
          </Box>
        </Box>
      </Paper>

      {record.currentStage === 'Hearing' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Hearing scheduled for {record.hearingDate || 'TBD'} — <Button size="small" component={Link} to="/disciplinary-cases">View All Cases</Button>
        </Alert>
      )}
      {record.outcome === 'Upheld' && (
        <Alert severity="error" sx={{ mb: 2 }} icon={<Warning />}>
          Case upheld — sanction issued per LRA Schedule 8
        </Alert>
      )}
      {record.outcome === 'Dismissed' && (
        <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircle />}>
          Case dismissed — no further action required
        </Alert>
      )}

      <ProcessActionsGrouped
        groups={[
          {
            title: 'Disciplinary Process',
            actions: [
              {
                label: 'Advance to Hearing',
                description: 'Move the case from Investigation to the Hearing stage. Employee will be scheduled for disciplinary hearing.',
                icon: <Gavel />,
                color: 'info',
                condition: record.currentStage === 'Investigation',
                onClick: () => advanceStage(),
                loading: isUpdating,
              },
              {
                label: 'Record Outcome - Upheld',
                description: 'Record that the disciplinary charge was upheld. Employee found guilty. Proceed to sanction.',
                icon: <CheckCircle />,
                color: 'success',
                condition: record.currentStage === 'Hearing',
                onClick: () => recordOutcome('Upheld'),
                loading: isUpdating,
              },
              {
                label: 'Record Outcome - Dismissed',
                description: 'Record that the disciplinary charge was dismissed. Employee found not guilty. Case will be closed.',
                icon: <Cancel />,
                color: 'error',
                variant: 'outlined',
                condition: record.currentStage === 'Hearing',
                onClick: () => recordOutcome('Dismissed'),
                loading: isUpdating,
              },
              {
                label: 'Close Case',
                description: 'Close this disciplinary case after appeal has been resolved or hearing completed.',
                icon: <Gavel />,
                color: 'info',
                condition: record.currentStage === 'Appeal',
                onClick: () => advanceStage(),
                loading: isUpdating,
              },
            ],
          },
          {
            title: 'Follow-up Actions',
            actions: [
              {
                label: 'Create PIP',
                description: 'Create a Performance Improvement Plan for this employee. Recommended after upheld disciplinary action.',
                icon: <TrendingUp />,
                color: 'info',
                condition: record.outcome === 'Upheld' && record.currentStage === 'Closed',
                onClick: () => {},
              },
              {
                label: 'Print Outcome Letter',
                description: 'Print or save the disciplinary outcome letter for records.',
                icon: <Assignment />,
                color: 'inherit',
                variant: 'outlined',
                condition: record.currentStage === 'Closed' || !!record.outcome,
                onClick: () => window.print(),
              },
            ],
          },
        ]}
        isLoading={isUpdating}
      />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Case Number', value: record.caseNumber, bold: true },
          { label: 'Employee', value: record.employee?.id },
          { label: 'Warning Type', value: record.warningType },
          { label: 'Stage', value: record.currentStage, chip: true },
          { label: 'Warning Type', value: record.warningType },
          { label: 'Incident Date', value: record.incidentDate },
          { label: 'Hearing Date', value: record.hearingDate || 'TBD' },
          { label: 'Outcome', value: record.outcome || 'Pending' },
        ].map(f => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={f.label}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">{f.label}</Typography>
              {f.chip ? (
                <Box sx={{ mt: 0.5 }}>
                  <Chip label={f.value} size="small" sx={{ bgcolor: STAGE_COLORS[record.currentStage ?? ''], color: 'white' }} />
                </Box>
              ) : (
                <Typography variant="body1" sx={{ fontWeight: f.bold ? 700 : 400, mt: 0.5 }}>{f.value ?? '—'}</Typography>
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Case Progress</Typography>
        <Grid container spacing={2}>
          {CASE_STAGES.map((stage, idx) => {
            const isCompleted = idx < stageIdx;
            const isCurrent = idx === stageIdx;
            const isNext = idx === stageIdx + 1;
            return (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={stage}>
                <Card
                  variant="outlined"
                  onClick={() => isNext && advanceStage()}
                  sx={{
                    cursor: isNext ? 'pointer' : 'default',
                    height: '100%',
                    borderLeft: '4px solid',
                    borderColor: isCompleted ? 'success.main' : isCurrent ? 'primary.main' : 'grey.300',
                    opacity: isNext ? 1 : isCompleted || isCurrent ? 1 : 0.6,
                    '&:hover': isNext ? { boxShadow: 2 } : {},
                    transition: 'all 0.2s',
                  }}
                >
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 28, height: 28, fontSize: 14, bgcolor: isCompleted ? 'success.main' : isCurrent ? 'primary.main' : 'grey.300' }}>
                          {stage === 'Investigation' ? <Search fontSize="small" /> : stage === 'Hearing' ? <Gavel fontSize="small" /> : stage === 'Appeal' ? <Assignment fontSize="small" /> : <CheckCircle fontSize="small" />}
                        </Avatar>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{stage}</Typography>
                      </Box>
                      {isCompleted ? (
                        <CheckCircle color="success" fontSize="small" />
                      ) : isCurrent ? (
                        <Schedule color="primary" fontSize="small" />
                      ) : (
                        <HourglassEmpty color="disabled" fontSize="small" />
                      )}
                    </Box>
                    <Typography variant="caption" color={isCompleted || isCurrent ? 'text.secondary' : 'text.disabled'} sx={{ mt: 0.5, display: 'block', ml: 5 }}>
                      {isCompleted ? `${stage} completed` : isCurrent ? 'In progress' : isNext ? 'Click to advance' : 'Pending'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Paper>

      {record.description && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'action.hover', borderLeft: '4px solid', borderColor: 'warning.main' }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Incident Description</Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>{record.description}</Typography>
        </Paper>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>LRA Schedule 8 Compliance Checklist</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>Click items to toggle their status</Typography>
            <Grid container spacing={1}>
              {LRA_CHECKLIST_ITEMS.map((check) => {
                const done = displayState[check.id] ?? false;
                return (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={check.id}>
                    <Box
                      onClick={() => toggleChecklistItem(check.id)}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1,
                        cursor: 'pointer', p: 0.5, borderRadius: 1,
                        '&:hover': { bgcolor: 'action.hover' },
                        userSelect: 'none',
                      }}
                    >
                      {done ? (
                        <CheckCircle color="success" fontSize="small" />
                      ) : (
                        <Schedule color="warning" fontSize="small" />
                      )}
                      <Typography variant="body2" sx={{ textDecoration: done ? 'none' : 'none' }}>
                        {check.label}
                      </Typography>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button component={Link} to="/pips" variant="outlined" size="small" endIcon={<ArrowForward />}>
          View Related PIPs
        </Button>
        <Button component={Link} to="/terminations" variant="outlined" size="small" endIcon={<ArrowForward />}>
          View Terminations
        </Button>
        <Button component={Link} to="/employees" variant="outlined" size="small">
          View Employee
        </Button>
      </Box>
    </Box>
  );
};

export const DisciplinaryShow = () => (
  <Show render={(props) => <DisciplinaryShowContent {...props} />} />
);

export const DisciplinaryCreate = () => {
  const [searchParams] = useSearchParams();
  const employeeId = searchParams.get('employeeId') || '';

  return (
    <Create>
      <SimpleForm>
        <Alert severity="info" sx={{ mb: 2 }}>
          Notice to attend disciplinary hearing must be issued per LRA Schedule 8
        </Alert>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Case Details</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
          <TextInput source="caseNumber" fullWidth />
          <ReferenceInput source="employee.id" reference="employees" fullWidth defaultValue={employeeId}>
            <SelectInput optionText="name" label="Employee" />
          </ReferenceInput>
          <TextInput source="department" fullWidth />
        <DateField source="incidentDate" />
        <SelectInput source="currentStage" choices={CASE_STAGES.map(s => ({ id: s, name: s }))} fullWidth defaultValue="Investigation" />
        <SelectInput source="warningType" choices={WARNING_TYPES.map(w => ({ id: w, name: w }))} fullWidth />
      </Box>
      <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>Hearing Details (LRA Schedule 8)</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
        <DateField source="hearingDate" label="Scheduled Hearing Date" />
        <TextInput source="hearingLocation" label="Hearing Location" fullWidth />
        <TextInput source="representative" label="Employee's Representative" fullWidth placeholder="Union rep or colleague" />
        <DateField source="noticeSentDate" label="Notice Sent Date" />
      </Box>
      <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>Incident Description</Typography>
      <TextInput source="description" multiline fullWidth placeholder="Describe the alleged misconduct or poor performance" />
      <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>Potential Sanctions</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
        <SelectInput source="potentialSanction" choices={['Verbal Warning', 'Written Warning', 'Final Written Warning', 'Suspend Without Pay', 'Dismissal'].map(s => ({ id: s, name: s }))} fullWidth label="Potential Sanction" />
      </Box>
    </SimpleForm>
  </Create>
  );
};

export const DisciplinaryEdit = () => (
  <Edit>
    <SimpleForm>
      <Alert severity="warning" sx={{ mb: 2 }}>
        Updating case details — all changes are logged in the audit trail
      </Alert>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Case Status</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
        <SelectInput source="currentStage" choices={CASE_STAGES.map(s => ({ id: s, name: s }))} fullWidth />
        <SelectInput source="outcome" choices={OUTCOMES.map(o => ({ id: o, name: o }))} fullWidth label="Outcome" />
        <SelectInput source="warningType" choices={WARNING_TYPES.map(w => ({ id: w, name: w }))} fullWidth />
      </Box>
      <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>Hearing Outcome</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
        <DateField source="hearingDate" label="Hearing Date" />
        <DateField source="outcomeDate" label="Outcome Date" />
        <TextInput source="sanctionIssued" label="Sanction Issued" fullWidth />
        <DateField source="sanctionEffectiveDate" label="Sanction Effective Date" />
      </Box>
      <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>Appeal</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
        <SelectInput source="appealLodged" choices={[{ id: 'Yes', name: 'Yes' }, { id: 'No', name: 'No' }]} fullWidth label="Appeal Lodged" />
        <DateField source="appealDate" label="Appeal Date" />
        <SelectInput source="appealOutcome" choices={['Upheld', 'Dismissed', 'Pending'].map(a => ({ id: a, name: a }))} fullWidth label="Appeal Outcome" />
      </Box>
      <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>Notes</Typography>
      <TextInput source="description" multiline fullWidth />
    </SimpleForm>
  </Edit>
);